import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const TOKEN_KEY = 'authToken';
const SETUP_TOKEN_KEY = 'setupToken';

export interface HelpDescItem {
  text?: string;
  heading?: string;
  items?: string[];
}

export interface HelpStep {
  title: string;
  description: HelpDescItem[];
}

export interface HelpSection {
  id: string;
  title: string;
  steps: HelpStep[];
}

export interface HelpContent {
  sections: HelpSection[];
  title: string;
  intro: string;
  readTime: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  // ── Normal auth ────────────────────────────────────────────────────────────
  logout(): void {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(SETUP_TOKEN_KEY);
    this.isAuthenticatedSubject.next(false);
  }

  getToken(): string | null {

    return sessionStorage.getItem(SETUP_TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return this.hasToken();
  }

  setToken(token: string): void {
    sessionStorage.setItem(TOKEN_KEY, token);
    this.isAuthenticatedSubject.next(true);
  }


  setSetupToken(token: string): void {
    sessionStorage.setItem(SETUP_TOKEN_KEY, token);
  }


  isSetupSession(): boolean {
    return !!sessionStorage.getItem(SETUP_TOKEN_KEY) && !sessionStorage.getItem(TOKEN_KEY);
  }


  completeSetup(): void {
    const setupToken = sessionStorage.getItem(SETUP_TOKEN_KEY);
    if (setupToken) {
      sessionStorage.setItem(TOKEN_KEY, setupToken);
      sessionStorage.removeItem(SETUP_TOKEN_KEY);
      this.isAuthenticatedSubject.next(true);
    }
  }

  clearSetupToken(): void {
    sessionStorage.removeItem(SETUP_TOKEN_KEY);
  }

  // ── User data helpers ──────────────────────────────────────────────────────
  get userData(): any {
    try { return JSON.parse(sessionStorage.getItem('userData') || '{}'); } catch { return {}; }
  }

  get permissions(): Record<string, boolean> {
    try { return JSON.parse(sessionStorage.getItem('permissions') || '{}'); } catch { return {}; }
  }

  get isAdmin(): boolean {
    return this.userData?.type === 'admin';
  }

  hasPermission(key: string): boolean {
    if (this.isAdmin) return true;
    return this.permissions[key] === true;
  }

  get aliasOrg(): string {
    return this.userData?.aliasOrg || 'Food Producer';
  }

  get assetId(): any {
    return Number(this.userData?.userDetails?.assetId) || '';
  }

  private hasToken(): boolean {
    return !!sessionStorage.getItem(TOKEN_KEY);
  }

  get country(): string {
    return this.userData?.userDetails?.country || 'Food Producer';
  }

  get userName(): string {
    return this.userData?.userName;
  }

  private helpModalTrigger = new BehaviorSubject<boolean>(false);
  helpModalTrigger$ = this.helpModalTrigger.asObservable();

  /** Identifies which page's content the help sidebar should show. */
  private helpPageContext = new BehaviorSubject<'user' | 'create-user' | 'create-role' | 'create-ledger' | 'clone-ledger' | 'asset-progress-detail'>('user');
  helpPageContext$ = this.helpPageContext.asObservable();

  private helpContent = new BehaviorSubject<HelpContent | null>(null);
  helpContent$ = this.helpContent.asObservable();

  triggerHelpHighlight(page: 'user' | 'create-user' | 'create-role' | 'create-ledger' | 'clone-ledger' | 'asset-progress-detail' = 'user') {
    this.helpPageContext.next(page);
    this.helpModalTrigger.next(true);
  }

  setHelpContent(page: 'user' | 'create-user' | 'create-role' | 'create-ledger' | 'clone-ledger' | 'asset-progress-detail', content: HelpContent) {
    this.helpPageContext.next(page);
    this.helpContent.next(content);
  }

  clearHelpHighlight() {
    this.helpModalTrigger.next(false);
  }

  openHelp(page: 'user' | 'create-user' | 'create-role' | 'create-ledger' | 'clone-ledger' | 'asset-progress-detail' = 'user') {
    this.helpPageContext.next(page);
    this.helpModalTrigger.next(true);
  }

  toggleHelpHighlight(page: 'user' | 'create-user' | 'create-role' | 'create-ledger' | 'clone-ledger' | 'asset-progress-detail' = 'user') {
    const isOpen = this.helpModalTrigger.value;
    if (!isOpen) {
      this.helpPageContext.next(page);
      this.helpModalTrigger.next(true);
      return;
    }

    if (this.helpPageContext.value !== page) {
      this.helpPageContext.next(page);
      this.helpModalTrigger.next(true);
      return;
    }

    this.helpModalTrigger.next(false);
  }
}
